// ═══════ Audit Log Card — Collapsible Chrome Bezel ═══════
export const AuditLogCard = ({
  auditOpen, setAuditOpen, auditLog, auditTotal, auditPage,
  auditLoading, fetchAuditLog, handleExportCsv, handleRollback, onSelectRun,
}) => (
  <div className="metal-card" data-testid="audit-log-card">
    <div className="metal-card-inner">
    {/* Clickable header row — toggles dropdown */}
    <div
      onClick={() => { setAuditOpen(o => !o); if (!auditOpen && auditLog.length === 0) fetchAuditLog(0); }}
      style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer',userSelect:'none'}}
      data-testid="audit-toggle"
    >
      <div className="beige-card-title" style={{marginBottom:0}}>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{color:'#7c6a48'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
        Audit Log
        <span style={{fontSize:'11px',fontWeight:400,color:'#9a9590',marginLeft:'8px'}}>{auditTotal > 0 ? `(${auditTotal})` : ''}</span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
        {auditOpen && (
          <>
            <button className="metal-btn" onClick={(e) => { e.stopPropagation(); fetchAuditLog(auditPage); }} disabled={auditLoading} style={{fontSize:'11px',padding:'4px 12px',width:'auto'}} data-testid="audit-refresh-btn">
              {auditLoading ? '...' : 'Refresh'}
            </button>
            <button className="metal-btn" onClick={(e) => { e.stopPropagation(); handleExportCsv(); }} style={{fontSize:'11px',padding:'4px 12px',width:'auto'}} data-testid="audit-export-btn">
              Download CSV
            </button>
          </>
        )}
        <svg viewBox="0 0 24 24" fill="none" stroke="#7c6a48" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{width:'20px',height:'20px',transition:'transform 0.3s ease',transform: auditOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>

    {/* Collapsible body */}
    <div style={{
      maxHeight: auditOpen ? '600px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.35s ease',
      marginTop: auditOpen ? '12px' : '0',
    }}>

    {/* Audit table */}
    <div style={{overflow:'auto',fontSize:'12px'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontFamily:"'SF Mono','Fira Code',monospace"}} data-testid="audit-table">
        <thead>
          <tr style={{borderBottom:'2px solid rgba(180,178,170,0.3)',textAlign:'left'}}>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Date/Time</th>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Store</th>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Run Type</th>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Status</th>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Run ID</th>
            <th style={{padding:'6px 8px',fontWeight:700,fontSize:'11px',color:'#6a6560'}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {auditLog.length === 0 ? (
            <tr><td colSpan={6} style={{padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:'12px'}}>No pipeline runs recorded yet.</td></tr>
          ) : auditLog.map((run, i) => {
            const runType = run.status === 'rollback' ? 'ROLLBACK' : (run.dry_run ? 'SAFE PREVIEW' : 'LIVE');
            const typeColor = run.status === 'rollback' ? '#7c3aed' : (run.dry_run ? '#3b82f6' : '#16a34a');
            const statusOk = run.status === 'success' || run.status === 'deployed' || run.status === 'complete' || run.status === 'rollback';
            const canRollback = run.status !== 'rollback';
            return (
              <tr
                key={run.id || i}
                onClick={() => onSelectRun && onSelectRun(run)}
                style={{
                  borderBottom: '1px solid rgba(180,178,170,0.15)',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,106,72,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                data-testid={`audit-row-${i}`}
              >
                <td style={{padding:'5px 8px',fontSize:'11px',color:'#4a4840',whiteSpace:'nowrap'}}>{run.started_at ? new Date(run.started_at).toLocaleString('en-US', {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                <td style={{padding:'5px 8px',fontSize:'11px',color:'#4a4840',maxWidth:'100px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{run.store_name || run.store_id || '-'}</td>
                <td style={{padding:'5px 8px',fontSize:'11px',fontWeight:700,color:typeColor}}>{runType}</td>
                <td style={{padding:'5px 8px',fontSize:'11px',color: statusOk ? '#16a34a' : '#dc2626'}}>{statusOk ? '\u2713' : '\u2717'} {run.status}</td>
                <td style={{padding:'5px 8px',fontSize:'10px',color:'#8a8680',maxWidth:'120px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{run.run_id || '-'}</td>
                <td style={{padding:'5px 8px'}}>
                  {canRollback && (
                    <button className="metal-btn" onClick={(e) => { e.stopPropagation(); handleRollback(run.run_id); }} style={{fontSize:'10px',padding:'3px 8px',width:'auto'}} data-testid={`rollback-btn-${i}`}>
                      Rollback
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* Pagination */}
    {auditTotal > 20 && (
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'10px',fontSize:'12px',color:'var(--text-muted)'}}>
        <span>Showing {auditPage * 20 + 1}–{Math.min((auditPage + 1) * 20, auditTotal)} of {auditTotal}</span>
        <div style={{display:'flex',gap:'6px'}}>
          <button className="metal-btn" onClick={() => fetchAuditLog(auditPage - 1)} disabled={auditPage === 0} style={{fontSize:'11px',padding:'3px 10px',width:'auto'}} data-testid="audit-prev-btn">Previous</button>
          <button className="metal-btn" onClick={() => fetchAuditLog(auditPage + 1)} disabled={(auditPage + 1) * 20 >= auditTotal} style={{fontSize:'11px',padding:'3px 10px',width:'auto'}} data-testid="audit-next-btn">Next</button>
        </div>
      </div>
    )}
    </div>{/* end collapsible body */}
    </div>
  </div>
);
